import React, { useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    FormControl,
} from '@mui/material';
import { isNullOrUndefined } from '../../../utils/utils';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DialogComponentProps } from '../../../models/quotations/NewQutationDialogProps';
import TextFieldStyled from '../../shared/TextFieldStyled';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import useQuotationActionsDialog from '../../../hooks/dialogs/useQuotationActionsDialog';
import useFullScreenLoading from '../../../hooks/useFullScreenLoading';
import useQuotation from '../../../hooks/quotations/useQuotation';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { mutate } from 'swr';
import { QUOTES_URL } from 'hooks/quotations/useQuotationsSWR';

const schema = yup.object().shape({
    name: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
});

const CreateNewQuotation: React.FC<DialogComponentProps> = (props) => {
    if (isNullOrUndefined(props)) {
        return null;
    }

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATION_DIALOG.NEW_QUOTATION_DIALOG',
    });
    const { products, handleSave } = props;
    const quotation = useQuotation();
    const [saving, setSaving] = useState(false);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });
    const quotationActionsDialog = useQuotationActionsDialog();
    const fullScreenLoading = useFullScreenLoading();

    const closeConfirmDialog = () => {
        reset();
        quotationActionsDialog.closeDialog();
    };

    const handleCreatedSuccessfully = (quotationId) => {
        reset();
        SnackbarUtils.success(t('CREATED_SUCCESSFULLY').toString());
        fullScreenLoading.setFullScreenLoading(false);
        handleSave(quotationId);
    };

    const onConfirmDialog: SubmitHandler<FormValues> = (data) => {
        setSaving(true);
        fullScreenLoading.setFullScreenLoading(true);

        quotation
            .createNewQuotation({
                items: quotation.convertProductsToQuotation(products),
                name: data.name,
            })
            .then((resp) => {
                handleCreatedSuccessfully(resp.id);
                mutate(QUOTES_URL);
            })
            .finally(() => {
                setSaving(false);
                fullScreenLoading.setFullScreenLoading(false);
            });
    };

    return (
        <form onSubmit={handleSubmit(onConfirmDialog)}>
            <DialogContent>
                <FormControl fullWidth>
                    <TextFieldStyled
                        autoFocus
                        error={!!errors.name}
                        label={t('NAME').toString()}
                        {...register('name')}
                        helperText={
                            errors.name
                                ? t(errors.name.message).toString()
                                : ' '
                        }
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={closeConfirmDialog}>
                    {t('CANCEL')}
                </Button>
                <PrimaryBtn size="small" type="submit" disabled={saving}>
                    {t('CREATE')}
                </PrimaryBtn>
            </DialogActions>
        </form>
    );
};

export default CreateNewQuotation;

type FormValues = {
    name: string;
};
